<template>
  <div v-if="events.length" class="ca-bullet-container ca-events-calendar-wrap">
    <section
      class="ca-events-calendar bg-theme-primary flex flex-col gap-size-7"
    >
      <CaModuleMessage
        theme="inverted"
        :label="model.label"
        :title="model.title"
        :title-heading-type="model.titleHeadingType || 'H2'"
        :description="model.description"
        :description-heading-type="model.descriptionHeadingType"
      />

      <div class="ca-events-calendar__main flex flex-col">
        <div class="ca-events-calendar__list flex">
          <CaEventCalendarCard
            v-for="(event, index) in events"
            :key="index"
            :statusLabel="event.statusLabel"
            :month="event.month"
            :day="event.day"
            :title="event.title"
            :title-heading-type="event.titleHeadingType"
            :location="event.location"
            :location-heading-type="event.locationHeadingType"
            :url="event.url"
            :target="event.target"
            :startDate="event.startDate"
          />
        </div>

        <div class="ca-events-calendar__footer">
          <component
            v-if="model.eventsCountText"
            :is="normalizeHeadingTag(model.eventsCountTextHeadingType, 'p')"
            class="ca-events-calendar__count caption-medium text-neutrals-inverted-60"
          >
            {{ model.eventsCountText }}
          </component>

          <LinkButton
            v-if="model.ctaText"
            class="ca-events-calendar__cta"
            :label="model.ctaText"
            icon="arrow_right"
            type="inverted"
            @click="handleCtaClick"
          />
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { LinkButton } from "ca-online-design-system";
import CaModuleMessage from "../../base/moduleMessage/CaModuleMessage.vue";
import CaEventCalendarCard from "../../base/cards/CaEventCalendarCard.vue";
import { normalizeHeadingTag } from "../../../utils/heading";

defineOptions({
  name: "CaEventsCalendar",
});

const isDev = import.meta.env.DEV;

const props = defineProps({
  model: {
    type: Object,
    default: null,
  },
});

const modelRef = ref(props.model ?? null);
const model = computed(() => modelRef.value || {});

const events = computed(() => (model.value.events ?? []).slice(0, 3));

const DESKTOP_QUERY = "(min-width: 1024px)";
const isDesktop = ref(false);
let mediaQuery = null;

function syncIsDesktop(event) {
  isDesktop.value = event.matches;
}

function handleCtaClick() {
  const url = model.value.ctaUrl;
  if (!url) return;

  if (model.value.ctaTarget === "_blank") {
    window.open(url, "_blank", "noopener,noreferrer");
    return;
  }

  window.location.href = url;
}

onMounted(async () => {
  if (typeof window !== "undefined" && window.matchMedia) {
    mediaQuery = window.matchMedia(DESKTOP_QUERY);
    isDesktop.value = mediaQuery.matches;
    mediaQuery.addEventListener("change", syncIsDesktop);
  }

  if (isDev && !modelRef.value) {
    const { mockEventsCalendar3 } =
      await import("./mocks/eventsCalendarMocks.js");
    modelRef.value = mockEventsCalendar3;
  }
});

onBeforeUnmount(() => {
  if (mediaQuery) mediaQuery.removeEventListener("change", syncIsDesktop);
});
</script>

<style scoped lang="scss">
.ca-events-calendar-wrap {
  padding-block: var(--size-8);
}

.ca-events-calendar {
  border-radius: var(--size-5);
  padding: var(--size-4);
}

.ca-events-calendar__list {
  flex-direction: column;
}

.ca-events-calendar__main,
.ca-events-calendar__list {
  gap: var(--size-4);
}

.ca-events-calendar__footer {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--size-4);
}

.ca-events-calendar__count {
  margin: 0;
  text-align: center;
}

.ca-events-calendar__cta {
  align-self: center;
}

@include ca-bp("md") {
  .ca-events-calendar-wrap {
    padding-block: var(--size-11);
  }

  .ca-events-calendar {
    border-radius: var(--size-8);
    padding: var(--size-6);
  }

  .ca-events-calendar__main,
  .ca-events-calendar__list {
    gap: var(--size-5);
  }

  .ca-events-calendar__list {
    flex-direction: row;
    align-items: stretch;
  }

  .ca-events-calendar__list > * {
    flex: 1 0 0;
    min-width: 0;
  }

  .ca-events-calendar__footer {
    position: relative;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    min-height: var(--size-8);
  }

  .ca-events-calendar__count {
    text-align: left;
  }

  .ca-events-calendar__cta {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    align-self: auto;
  }
}
</style>
